import { FileText, FileSearch, Loader2 } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { lettersApi } from '../../services/api';

export function CreateLetterSelection() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isCheckingLetter, setIsCheckingLetter] = useState(false);
  const letterId = searchParams.get('letterId');
  const useTemplateId = searchParams.get('useTemplateId');
  const canAccessUploadPdf = true;

  useEffect(() => {
    if (!useTemplateId || letterId) return;
    const params = new URLSearchParams(searchParams);
    params.set('page', 'letters-create-fresh');
    setSearchParams(params);
  }, [letterId, searchParams, setSearchParams, useTemplateId]);

  // Auto-detect letter type and redirect if letterId is present
  useEffect(() => {
    if (!letterId) return;

    const autoRedirect = async () => {
      try {
        setIsCheckingLetter(true);
        const response = await lettersApi.get(letterId);
        const letter = response?.data;
        
        if (letter) {
          // Check if it's a PDF-based letter by looking for PDF page markers
          const isPdfLetter = letter.body_html?.includes('sepl-pdf-page');
          
          // Navigate to the appropriate editor while preserving letterId
          const params = new URLSearchParams(searchParams);
          if (isPdfLetter && canAccessUploadPdf) {
            params.set('page', 'letters-upload-pdf');
          } else {
            params.set('page', 'letters-create-fresh');
          }
          setSearchParams(params);
        } else {
          // No letter returned: stop the loading spinner so the user is not
          // stuck on an infinite "Opening letter editor..." screen.
          console.error('Letter not found for id:', letterId);
          setIsCheckingLetter(false);
        }
      } catch (err) {
        console.error('Failed to auto-detect letter type:', err);
        setIsCheckingLetter(false);
      }
    };

    autoRedirect();
  }, [canAccessUploadPdf, letterId, searchParams, setSearchParams]);

  const handleNavigate = (page: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', page);
    params.delete('letterId');
    params.delete('openLetterId');
    params.delete('useTemplateId');
    setSearchParams(params);
  };

  // Show loading state while detecting letter type
  if (isCheckingLetter) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-50 min-h-screen">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-violet-600 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">Opening letter editor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-gray-50">
      <div className="w-full max-w-6xl mx-auto px-4 py-8 md:py-10">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Create a Letter</h1>
          <p className="text-lg text-gray-600">Choose how you want to create your letter</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 items-stretch">
          {/* Create Fresh Letter */}
          <button
            onClick={() => handleNavigate('letters-create-fresh')}
            className="group min-w-0 h-full bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 p-6 lg:p-8 text-left border-2 border-transparent hover:border-blue-500"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition">
                <FileText size={28} className="text-blue-600" />
              </div>
            </div>
            <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mb-3 break-words">Create Fresh Letter</h2>
            <p className="text-gray-600 mb-6 break-words">
              Start from scratch with the official letter template. Add your content, customize the layout, and generate a professional letter with signature support.
            </p>
            <div className="flex items-center text-blue-600 font-semibold group-hover:gap-2 transition-all">
              <span>Get Started</span>
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </button>

          {/* Use Templates */}
          <button
            onClick={() => handleNavigate('letters-view-templates')}
            className="group min-w-0 h-full bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 p-6 lg:p-8 text-left border-2 border-transparent hover:border-green-500"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="w-14 h-14 bg-green-100 rounded-lg flex items-center justify-center group-hover:bg-green-200 transition">
                <FileSearch size={28} className="text-green-600" />
              </div>
            </div>
            <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mb-3 break-words">Use Templates</h2>
            <p className="text-gray-600 mb-6 break-words">
              Open the template library, preview approved templates, and use one to prefill the Create Letter editor with the selected design.
            </p>
            <div className="flex items-center text-green-600 font-semibold group-hover:gap-2 transition-all">
              <span>Browse Templates</span>
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </button>

          {canAccessUploadPdf && (
            <button
              onClick={() => handleNavigate('letters-upload-pdf')}
              className="group min-w-0 h-full bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 p-6 lg:p-8 text-left border-2 border-transparent hover:border-violet-500"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-14 h-14 bg-violet-100 rounded-lg flex items-center justify-center group-hover:bg-violet-200 transition">
                  <FileText size={28} className="text-violet-600" />
                </div>
              </div>
              <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mb-3 break-words">Upload PDF Document</h2>
              <p className="text-gray-600 mb-6 break-words">
                Upload a finished PDF letter and continue with the same latest letterhead and footer branding, then place the signature box.
              </p>
              <div className="flex items-center text-violet-600 font-semibold group-hover:gap-2 transition-all">
                <span>Open Upload</span>
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>
          )}
        </div>

        {/* Info Section */}
        <div className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="font-semibold text-gray-900 mb-3">Tips:</h3>
          <ul className="space-y-2 text-gray-700 text-sm">
            <li className="flex gap-2">
              <span className="text-blue-600 font-bold">•</span>
              <span>Create fresh letters or start from reusable templates</span>
            </li>
            <li className="flex gap-2">
              <span className="text-blue-600 font-bold">•</span>
              <span>You can add signature boxes on any page with drag-and-drop positioning</span>
            </li>
            <li className="flex gap-2">
              <span className="text-blue-600 font-bold">•</span>
              <span>Letters are automatically saved as drafts as you work</span>
            </li>
            <li className="flex gap-2">
              <span className="text-blue-600 font-bold">•</span>
              <span>Submit for approval when ready, or keep it as a draft</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
